import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Scale, AlertCircle, ShieldAlert, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { salesService } from '@/services/salesService';
import { cycleService } from '@/services/cycleService';
import { ScaleWeightGrid } from '@/components/sales/ScaleWeightGrid';
import { Combobox } from '@/components/common/Combobox';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/lib/utils';
import {
  BuyerRecord,
  ProductionCycleRecord,
  TransactionCategoryRecord,
  RateRuleRecord,
  ScaleItemInput,
  PigSaleRecord,
} from '@/types/database';

interface PigSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (sale: PigSaleRecord) => void;
  cycleId?: string | null;
  cycleCode?: string;
}

export const PigSaleModal: React.FC<PigSaleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
}) => {
  const { isAdmin } = useAuth();

  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [buyers, setBuyers] = useState<BuyerRecord[]>([]);
  const [categories, setCategories] = useState<TransactionCategoryRecord[]>([]);
  const [activeRateRule, setActiveRateRule] = useState<RateRuleRecord | null>(null);

  // Form State
  const [saleDate, setSaleDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [buyerId, setBuyerId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [shrinkageRateApplied, setShrinkageRateApplied] = useState<number>(0);
  const [isShrinkageOverridden, setIsShrinkageOverridden] = useState<boolean>(false);
  const [scaleTicketNo, setScaleTicketNo] = useState<string>('');
  const [gatePassNo, setGatePassNo] = useState<string>('');
  const [vehiclePlateNo, setVehiclePlateNo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Live Available Headcount for selected cycle
  const [availableLiveHeadcount, setAvailableLiveHeadcount] = useState<number | null>(null);
  const [loadingCycleHeadcount, setLoadingCycleHeadcount] = useState<boolean>(false);

  // Admin Override
  const [adminOverride, setAdminOverride] = useState<boolean>(false);
  const [overrideReason, setOverrideReason] = useState<string>('');

  // Scale Items
  const [scaleItems, setScaleItems] = useState<ScaleItemInput[]>([
    { sequence_no: 1, head_count: 1, gross_weight_kg: 0, price_per_kg: 135.0, notes: '' },
  ]);

  // Photo Attachment
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cycleId) {
      setSelectedCycleId(cycleId);
    }
  }, [cycleId]);

  // Load buyers, cycles, and categories
  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    salesService.getBuyers().then((bList) => {
      setBuyers(bList);
      if (bList.length > 0 && !buyerId) {
        setBuyerId(bList[0].id);
      }
    });

    salesService.getTransactionCategories().then((catList) => {
      setCategories(catList);
      const defaultCat = catList.find((c) => c.code === '1010') || catList[0];
      if (defaultCat && !categoryId) {
        setCategoryId(defaultCat.id);
      }
    });

    if (!cycleId) {
      cycleService.getCycles().then((cList) => {
        setCycles(cList);
        if (cList.length > 0 && !selectedCycleId) {
          setSelectedCycleId(cList[0].id);
        }
      });
    }
  }, [isOpen, cycleId]);

  // Load live reactive headcount and rate rule suggestion whenever cycle or saleDate changes
  useEffect(() => {
    const targetCycleId = cycleId || selectedCycleId;
    if (!targetCycleId || !isOpen) return;

    setLoadingCycleHeadcount(true);
    cycleService
      .getCycleDetail(targetCycleId)
      .then((detail) => {
        setAvailableLiveHeadcount(detail.headcount.expectedCurrentHeadcount);

        // Fetch rate rule suggestion
        const farmId = detail.cycle.farm_id;
        const growerId = detail.cycle.grower_id;
        salesService.getRateRuleSuggestion(saleDate, farmId, growerId).then((rule) => {
          setActiveRateRule(rule);
          if (rule && !isShrinkageOverridden) {
            setShrinkageRateApplied(Number(rule.rate_value) || 0);
          }
        });
      })
      .catch((err) => {
        console.warn('Error fetching cycle detail for headcount:', err);
      })
      .finally(() => {
        setLoadingCycleHeadcount(false);
      });
  }, [selectedCycleId, cycleId, saleDate, isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const validItems = scaleItems.filter((i) => Number(i.gross_weight_kg) > 0);
  const totalHead = validItems.reduce(
    (sum, i) => sum + Math.max(1, parseInt(String(i.head_count || 1), 10) || 1),
    0
  );
  const isOverSelling = availableLiveHeadcount !== null && totalHead > availableLiveHeadcount;

  const buyerOptions = useMemo(() => {
    return buyers.map((b) => ({
      value: b.id,
      label: b.buyer_name,
    }));
  }, [buyers]);

  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
      badge: c.status,
    }));
  }, [cycles]);

  const categoryOptions = useMemo(() => {
    return categories.map((c) => ({
      value: c.id,
      label: `${c.code} — ${c.name}`,
    }));
  }, [categories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const targetCycleId = cycleId || selectedCycleId;
    if (!targetCycleId) {
      setErrorMsg('Please select a Production Cycle.');
      return;
    }
    if (!buyerId) {
      setErrorMsg('Please select a Buyer.');
      return;
    }
    if (validItems.length === 0) {
      setErrorMsg('Please enter at least one scale weight line.');
      return;
    }
    if (validItems.some((i) => !i.price_per_kg || Number(i.price_per_kg) <= 0)) {
      setErrorMsg('Please enter a valid base price per kg for all scale weight lines.');
      return;
    }

    if (isOverSelling) {
      if (!adminOverride) {
        setErrorMsg(
          `Over-selling blocked: Entered ${totalHead} head exceeds the cycle's available live balance (${availableLiveHeadcount} head).`
        );
        return;
      }
      if (!overrideReason.trim()) {
        setErrorMsg('Please provide a mandatory reason for the Admin over-selling override.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const newSale = await salesService.createPigSale({
        cycle_id: targetCycleId,
        buyer_id: buyerId,
        category_id: categoryId || null,
        sale_date: saleDate,
        price_per_kg: Number(validItems[0]?.price_per_kg) || 135.0,
        shrinkage_rate_applied: shrinkageRateApplied,
        rate_rule_id: activeRateRule?.id || null,
        scale_ticket_no: scaleTicketNo || null,
        gate_pass_no: gatePassNo || null,
        vehicle_plate_no: vehiclePlateNo || null,
        notes,
        scale_items: validItems,
        attached_file: attachedFile,
        admin_override: adminOverride && isOverSelling,
        override_reason: overrideReason,
      });

      if ((newSale as any)?.attachment_error) {
        alert(
          `Pig sale was recorded successfully, but scale ticket photo upload failed (${(newSale as any).attachment_error}). You can re-attach the document from the Documents tab.`
        );
      }

      onSuccess(newSale);
    } catch (err: any) {
      console.error('[PigSaleModal] Submit error:', err);
      setErrorMsg(err.message || 'Failed to save pig sale.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
              <Tag className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Pig Sale / Scale Chute Lift
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Rapid scale-weight logging, dynamic shrinkage, and buyer checkout.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-3 space-y-4 no-scrollbar">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="font-medium">{errorMsg}</div>
            </div>
          )}

          {/* Section 1: Sale Order Header */}
          <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
            {/* Row 1: Cycle, Buyer, Sale Date, Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Production Cycle */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Production Cycle *
                </label>
                {cycleId ? (
                  <div className="p-2 bg-white border border-slate-200 rounded-lg font-mono font-bold text-xs text-slate-800 flex items-center justify-between min-h-[38px]">
                    <span>{cycleCode || cycleId}</span>
                    {availableLiveHeadcount !== null && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        {availableLiveHeadcount} live hd
                      </span>
                    )}
                  </div>
                ) : (
                  <Combobox
                    options={cycleOptions}
                    value={selectedCycleId}
                    onChange={setSelectedCycleId}
                    placeholder="Select Cycle..."
                    searchPlaceholder="Search cycle..."
                    disabled={submitting}
                  />
                )}
                {availableLiveHeadcount !== null && !cycleId && (
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">
                    Live balance:{' '}
                    <span className="font-bold text-emerald-700">{availableLiveHeadcount} head</span> available
                  </p>
                )}
              </div>

              {/* Buyer */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Buyer / Customer *
                </label>
                <Combobox
                  options={buyerOptions}
                  value={buyerId}
                  onChange={setBuyerId}
                  placeholder="Select Buyer..."
                  searchPlaceholder="Search buyer..."
                  disabled={submitting}
                />
              </div>

              {/* Sale Date */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Sale Date *
                </label>
                <Input
                  type="date"
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  required
                  className="h-9 text-xs bg-white"
                />
              </div>

              {/* Sales Category / Account */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Account / Category
                </label>
                <Combobox
                  options={categoryOptions}
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder="Select Category..."
                  searchPlaceholder="Search category..."
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Row 2: Shrinkage Rate, Scale Ticket, Gate Pass, Vehicle Plate */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-slate-200">
              {/* Applied Shrinkage Rate */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    Shrinkage Rate (kg/hd)
                  </label>
                  {activeRateRule && !isShrinkageOverridden && (
                    <span className="text-[9px] px-1 bg-amber-100 text-amber-800 rounded font-medium">
                      Rule: {activeRateRule.rate_value} {activeRateRule.rate_unit}
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    inputMode="decimal"
                    value={shrinkageRateApplied}
                    onChange={(e) => {
                      setShrinkageRateApplied(parseFloat(e.target.value) || 0);
                      setIsShrinkageOverridden(true);
                    }}
                    className="h-9 text-xs font-mono text-amber-900 bg-white"
                  />
                  {isShrinkageOverridden && activeRateRule && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShrinkageRateApplied(Number(activeRateRule.rate_value) || 0);
                        setIsShrinkageOverridden(false);
                      }}
                      className="h-9 px-2 text-[10px] text-slate-500 hover:text-slate-800"
                      title="Reset to active rule rate"
                    >
                      Reset
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                  {activeRateRule
                    ? `Suggested: ${activeRateRule.rule_name}`
                    : 'No active rule; manual entry.'}
                </p>
              </div>

              {/* Scale Ticket / DR # */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Scale Ticket / DR #
                </label>
                <Input
                  type="text"
                  placeholder="e.g. ST-2026-0412"
                  value={scaleTicketNo}
                  onChange={(e) => setScaleTicketNo(e.target.value)}
                  className="h-9 text-xs font-mono bg-white"
                />
              </div>

              {/* Gate Pass # */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Gate Pass #
                </label>
                <Input
                  type="text"
                  placeholder="e.g. GP-0891"
                  value={gatePassNo}
                  onChange={(e) => setGatePassNo(e.target.value)}
                  className="h-9 text-xs font-mono bg-white"
                />
              </div>

              {/* Vehicle Plate # / Driver */}
              <div>
                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                  Vehicle Plate # / Driver
                </label>
                <Input
                  type="text"
                  placeholder="e.g. ABC-1234 (R. Cruz)"
                  value={vehiclePlateNo}
                  onChange={(e) => setVehiclePlateNo(e.target.value)}
                  className="h-9 text-xs font-mono bg-white"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Over-Selling Warning & Admin Override */}
          {isOverSelling && (
            <div className="p-3.5 rounded-lg bg-amber-50 border border-amber-300 space-y-2.5 text-xs text-amber-900">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-950">Over-Selling Headcount Detected</h4>
                  <p className="mt-0.5">
                    You have entered <span className="font-bold font-mono">{totalHead} head</span>, but this cycle only has{' '}
                    <span className="font-bold font-mono">{availableLiveHeadcount} head</span> live available.
                  </p>
                </div>
              </div>

              {isAdmin ? (
                <div className="pt-2 border-t border-amber-200 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="adminOverrideCheck"
                      checked={adminOverride}
                      onChange={(e) => setAdminOverride(e.target.checked)}
                      className="rounded border-amber-400 text-amber-700 focus:ring-amber-600 h-4 w-4"
                    />
                    <label htmlFor="adminOverrideCheck" className="font-bold text-amber-950 cursor-pointer">
                      Admin Override: Permit posting over-selling lift
                    </label>
                  </div>

                  {adminOverride && (
                    <div>
                      <label className="block text-[10px] font-bold text-amber-900 uppercase tracking-wider mb-1">
                        Mandatory Override Reason *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g. Unrecorded transfer in from sow unit pen #4"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        required={adminOverride}
                        className="h-9 text-xs bg-white border-amber-300"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="font-semibold text-red-700 pt-1 border-t border-amber-200">
                  Operations role cannot post an impossible negative physical balance. Please check your scale rows or contact an administrator.
                </p>
              )}
            </div>
          )}

          {/* Section 3: Rapid Numeric Scale Weights Grid */}
          <ScaleWeightGrid
            items={scaleItems}
            onChange={setScaleItems}
            shrinkageRateApplied={shrinkageRateApplied}
            disabled={submitting}
          />

          {/* Section 4: File / Ticket Photo Upload & Remarks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                Attach Scale Ticket Photo / Scan
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="h-9 text-xs file:h-7 file:text-xs file:py-0 file:px-2 file:bg-slate-100 file:border-0"
                />
                {attachedFile && (
                  <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1 shrink-0">
                    <Check className="w-3.5 h-3.5" /> Attached
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                Sale Remarks / Notes
              </label>
              <Input
                type="text"
                placeholder="Optional notes or buyer comments"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-9 text-xs bg-white"
              />
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || (isOverSelling && !adminOverride)}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold gap-1.5 px-4 shadow-xs"
            >
              {submitting ? 'Saving Sale...' : 'Finalize & Post Sale'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
